import { format } from "date-fns";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { RecurrenceExceptionError } from "@/api/remoteBookingApi";
import type { OccurrenceEntry } from "./generateOccurrenceList";

interface RecurrenceExceptionConfirmDialogProps {
  entry: OccurrenceEntry;
  open: boolean;
  pending: boolean;
  error: Error | null;
  /** True when excluding this occurrence is within 24h and therefore chargeable (C1). */
  willBeCharged?: boolean;
  /** Elevated users may waive the charge. */
  canWaive?: boolean;
  waive?: boolean;
  onWaiveChange?: (waive: boolean) => void;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

function humanReason(error: Error | null): string | null {
  if (!error) return null;
  if (error instanceof RecurrenceExceptionError) {
    if (error.reason === "clash") {
      const detail = error.detail as { message?: string } | undefined;
      return detail?.message
        ? `Cannot re-include — ${detail.message}`
        : "Cannot re-include — another booking already occupies this slot. Cancel or move it first.";
    }
    if (error.reason === "date_already_excluded") return "This date is already excluded.";
    if (error.reason === "date_not_excluded") return "This date is not currently excluded.";
    if (error.reason === "date_in_past") return "You cannot change an exception on a date that has already passed.";
    if (error.reason === "series_cancelled") return "This booking series has been cancelled.";
    if (error.reason === "no_recurrence") return "This booking has no recurrence pattern.";
  }
  return error.message || "Something went wrong — please try again.";
}

export function RecurrenceExceptionConfirmDialog({
  entry,
  open,
  pending,
  error,
  willBeCharged = false,
  canWaive = false,
  waive = false,
  onWaiveChange,
  onConfirm,
  onClose,
}: RecurrenceExceptionConfirmDialogProps) {
  const friendlyDate = format(entry.date, "EEEE d MMMM yyyy");
  const isExcluded = entry.state === "excluded";
  const title = isExcluded ? "Re-include this date?" : "Exclude this date?";
  const description = isExcluded
    ? `Restore the booking on ${friendlyDate}. The original recurrence will resume on this date. Subject to availability.`
    : `Exclude ${friendlyDate} from this repeat booking? The room and any attached amenities become free for others to book if you proceed. The booking series itself remains unchanged and this booking is removed.`;
  const actionLabel = isExcluded ? "Re-include" : "Exclude";
  const reason = humanReason(error);
  const showCharge = !isExcluded && willBeCharged;

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {showCharge && (
          <div
            data-testid="exclusion-charge-warning"
            className="flex gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-3"
          >
            <AlertTriangle className="mt-0.5 size-5 flex-shrink-0 text-amber-600" />
            <div>
              <h3 className="mb-1 text-sm font-semibold text-amber-900">Cancellation charges apply</h3>
              <p className="text-xs text-amber-800">
                This occurrence starts within 24 hours, so excluding it is chargeable — the same as a
                late cancellation.
              </p>
              {canWaive && (
                <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900">
                  <Checkbox
                    data-testid="exclusion-waive-toggle"
                    checked={waive}
                    onCheckedChange={(v) => onWaiveChange?.(v === true)}
                  />
                  Waive this charge
                </label>
              )}
            </div>
          </div>
        )}
        {reason && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            data-testid="recurrence-exception-error"
          >
            {reason}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending} onClick={onClose}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={(event) => {
              event.preventDefault();
              void onConfirm();
            }}
          >
            {pending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner className="h-4 w-4" />
                Working…
              </span>
            ) : (
              actionLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default RecurrenceExceptionConfirmDialog;
